import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getFromServerTopic } from "@utility/slice";
import { IDLE, LOADING } from "@constants";
import moment from "moment";

const SLICE = "curtailmentPrograms";

const action = (name) => `${SLICE}/${name}`;

export const getFromServer = (action, payloadFn) =>
  getFromServerTopic({ topic: SLICE, action, payloadFn });

export const getAllBaselineMethods = getFromServerTopic({
  topic: "demandResponse",
  action: "getAllBaselineMethods",
  payloadFn: async (payload) => payload,
});

export const getAllCurtailmentPrograms = getFromServer("getAllPrograms");

export const deleteCurtailmentProgram = getFromServerTopic({
  topic: SLICE,
  action: "deleteCurtailmentProgram",
  payloadFn: async (payload) => payload,
});

export const saveCurtailmentProgram = getFromServerTopic({
  topic: SLICE,
  action: "saveProgram",
  payloadFn: async (payload) => payload,
});

export const getAllCurtailmentProgramAdmins = getFromServer(
  "getAllProgramAdmins",
);

export const getAllCurtailmentProgramPrices = getFromServer(
  "getAllProgramPrices",
);

export const saveCurtailmentProgramPrice = getFromServerTopic({
  topic: SLICE,
  action: "saveProgramPrice",
  payloadFn: async (payload) => payload,
});

export const deleteCurtailmentProgramPrice = getFromServerTopic({
  topic: SLICE,
  action: "deleteProgramPrice",
  payloadFn: async (payload) => payload,
});

export const initializeProgramManagementPage = createAsyncThunk(
  action("initializeProgramManagementPage"),
  async (arg, { dispatch }) =>
    Promise.all([
      dispatch(getAllBaselineMethods()),
      dispatch(getAllCurtailmentPrograms()),
      dispatch(getAllCurtailmentProgramPrices()),
      dispatch(getAllCurtailmentProgramAdmins()),
    ]),
);

export const slice = createSlice({
  name: SLICE,
  initialState: {
    baselineMethods: [],
    programs: [],
    programAdmins: [],
    programPrices: [],
    status: LOADING,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllBaselineMethods.fulfilled, (state, action) => {
        state.baselineMethods = action.payload.response;
      })
      .addCase(getAllCurtailmentPrograms.fulfilled, (state, action) => {
        state.programs = action.payload.response;
      })
      .addCase(deleteCurtailmentProgram.fulfilled, (state, action) => {
        state.programs = state.programs.filter(
          (program) => program.id !== action.payload.response,
        );
      })
      .addCase(saveCurtailmentProgram.fulfilled, (state, action) => {
        const program = action.payload.response.program;
        if (action.payload.response.method === "save") {
          // Add the new record
          state.programs = [
            ...state.programs,
            { ...program, programId: action.payload.response.insertId },
          ];
        } else {
          // Replace the old record with the new one
          state.programs = [
            ...state.programs.filter(
              (_program) => _program.programId !== program.programId,
            ),
            program,
          ];
        }
      })
      .addCase(getAllCurtailmentProgramAdmins.fulfilled, (state, action) => {
        state.programAdmins = action.payload.response;
      })
      .addCase(getAllCurtailmentProgramPrices.fulfilled, (state, action) => {
        state.programPrices = action.payload.response;
      })
      .addCase(saveCurtailmentProgramPrice.fulfilled, (state, action) => {
        if (
          action?.payload?.response &&
          action.payload.response.errors.length === 0
        ) {
          const { price } = action.payload.response;
          if (action.payload.response.method === "save") {
            // Add the new record
            state.programPrices = [
              ...state.programPrices,
              {
                ...price,
                programPriceId: action.payload.response.insertId,
              },
            ].sort((a, b) =>
              moment(a.effectiveStart).isBefore(moment(b.effectiveStart)),
            );
          } else {
            // Replace the old record with the new one
            state.programPrices = [
              ...state.programPrices.filter(
                (_price) => price.programPriceId !== _price.programPriceId,
              ),
              price,
            ].sort((a, b) =>
              moment(a.effectiveStart).isBefore(moment(b.effectiveStart)),
            );
          }
        } else {
          console.error(`An error occurred.`, action.payload);
        }
      })
      .addCase(deleteCurtailmentProgramPrice.fulfilled, (state, action) => {
        state.programPrices = state.programPrices.filter(
          (programPrice) =>
            programPrice.programPriceId !==
            action.payload.response.price.programPriceId,
        );
      })
      .addCase(initializeProgramManagementPage.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(initializeProgramManagementPage.fulfilled, (state) => {
        state.status = IDLE;
      });
  },
});

export default slice.reducer;
